import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"
import { planningMutation } from "../planning_mutation"
import { urlWithQuery } from "../helpers/url_with_query"

// ONE delegated controller for the whole employee-centric capacity board.
//
// Same rationale as planning-board-drop: this layout renders employees × dates cells, and
// instantiating a Stimulus controller per cell froze the page for ~30s. This single controller
// lives on the board container and resolves the target cell from the event via `closest`, so
// cells stay plain [data-planning-cell] markers with no listeners of their own.
//
// It handles the ways to plan from an employee row:
//   - drop a Baustelle/Aufgabe card from the inverted panel onto a cell
//   - click an empty cell            → dialog in "plannablePick" mode (employee fixed)
//   - drag horizontally across cells → a ghost bar previews the range, then the same dialog with
//                                      von..bis pre-filled to it
// …and the ways to change an existing bar:
//   - click it            → the shared dialog in edit mode (reconciles the run via set_span)
//   - drag its body       → move the run: sideways shifts the dates, onto another row reassigns it
//   - drag an edge        → extend / shorten that end
// Capacity blocker bars (data-blocker-id) take the same gestures, routed to the blocker endpoints
// and the blocker dialog; a blocker type chip from the panel drops a one-day blocker.
//
// In HOURLY mode (Stundenmodus) the day-mode gestures are swapped for focused-day time editing:
//   - drop a card on the focused day → a timed segment at the pointer's time (default length)
//   - click a segment                → the exact-times popover (von/bis, remove)
//   - drag a segment / its edge      → move / resize on the hour scale, 30-min snap
// Context-day cells are read-only there — their headers refocus the day instead.
//
// Narrowing which employees are shown is a server-side filter (Filter panel), not a client-side
// search, so a filtered board is a shareable URL and survives a reload.
const SNAP_MINUTES = 30
// The drop length when the panel's Dauer chips have never been touched.
const FALLBACK_DROP_MINUTES = 30
const DURATION_STORAGE_KEY = "planning_hourly_drop_minutes"

export default class extends Controller {
  static targets = []
  static values = {
    url: String,
    rangeUrl: String,
    setSpanUrl: String,
    clearSpanUrl: String,
    reassignSpanUrl: String,
    blockerUrl: String,
    canPlan: Boolean,
    rangeStart: String,
    rangeEnd: String,
    // [{start, end}] per rendered column, so a pointer x maps onto the date the planner sees.
    periods: Array,
    hourly: Boolean,
    focusDate: String,
    windowStart: Number,
    windowEnd: Number,
    // Confirm texts for a bar's ✕ and the PT line of a bar's hover card, with %{…} placeholders:
    // the bars carry only their identity, label and PT (see AllocationBarComponent#bar_data).
    removeTitle: String,
    removeBody: String,
    tooltipPt: String,
  }

  connect() {
    this.activeCell = null
    this.selection = null
    this.drag = null
    this.hourlyDrag = null
    this.popoverSegment = null
    this.suppressClick = false
    this.boundSelectMove = this.selectMove.bind(this)
    this.boundSelectEnd = this.selectEnd.bind(this)
    this.boundRemoveSelectionGhost = this.removeSelectionGhost.bind(this)
    this.boundDragMove = this.dragMove.bind(this)
    this.boundDragEnd = this.dragEnd.bind(this)
    this.boundHourlyDragMove = this.hourlyDragMove.bind(this)
    this.boundHourlyDragEnd = this.hourlyDragEnd.bind(this)
    this.boundGestureCancel = this.gestureCancel.bind(this)
    this.boundPopoverDismiss = this.popoverDismiss.bind(this)
    this.boundFillBarTooltip = this.fillBarTooltip.bind(this)
    // Capture phase: runs before the board's planning-tooltip-delegate looks for the payload.
    this.element.addEventListener("mouseover", this.boundFillBarTooltip, true)
    this.wirePopover()
  }

  disconnect() {
    this.element.removeEventListener("mouseover", this.boundFillBarTooltip, true)
    this.clear()
    this.endSelectionListeners()
    this.removeSelectionGhost()
    this.endDragListeners()
    this.endHourlyDragListeners()
    this.closePopover()
  }

  // The absence bands sit over the cells and take pointer events for their hover card, so a plain
  // `closest` would miss the cell underneath them. Look through the stack at that point instead.
  cellFor(event) {
    const direct = event.target.closest?.("[data-planning-cell]")
    if (direct) return direct
    if (event.target.closest?.("[data-planning-bar]")) return null

    const stack = document.elementsFromPoint(event.clientX, event.clientY)
    return stack.find((el) => el.matches?.("[data-planning-cell]")) || null
  }

  highlight(cell) {
    if (this.activeCell === cell) return
    this.clear()
    cell?.classList.add("ring-2", "ring-inset", "ring-green-400", "bg-green-50")
    this.activeCell = cell
  }

  clear() {
    this.activeCell?.classList.remove("ring-2", "ring-inset", "ring-green-400", "bg-green-50")
    this.activeCell = null
  }

  dragover(event) {
    if (!this.canPlanValue) return
    const cell = this.cellFor(event)
    if (!cell) return
    // Hourly mode plans times on the focused day only; context cells don't accept drops.
    if (this.hourlyValue && cell.dataset.cellDate !== this.focusDateValue) return
    // Hourly: the ghost shows exactly what the drop would create. No room at the pointer →
    // no ghost and no preventDefault, so the browser itself refuses the drop there.
    if (this.hourlyValue) {
      const slot = this.slotAtPointer(cell, event.clientX)
      if (!slot) {
        this.hideDropGhost()
        return
      }
      this.showDropGhost(cell, slot)
    }
    // preventDefault on dragover is what allows the subsequent drop to fire.
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    this.highlight(cell)
  }

  // dragleave bubbles up from child nodes as the pointer moves between cells, so only clear the
  // highlight when the pointer actually leaves the board container.
  dragleave(event) {
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) return
    this.clear()
    this.hideDropGhost()
  }

  drop(event) {
    if (!this.canPlanValue) return
    const cell = this.cellFor(event)
    if (!cell) return
    event.preventDefault()
    this.clear()

    try {
      const payload = event.dataTransfer.getData("application/json")
      if (!payload) return

      const data = JSON.parse(payload)
      if (data.resourceType === "CapacityBlockerType" && data.resourceId) {
        this.hideDropGhost()
        this.dropBlocker(cell, data)
        return
      }
      // This layout's panel offers Baustellen and Aufgaben; employee/fleet/trade payloads belong
      // to the site-grouped boards and are ignored here.
      if (!data.resourceId || (data.resourceType !== "Site" && data.resourceType !== "Task")) return

      // Aggregated period cell (week/month/quarter): don't plan a single day — open the dialog so
      // the user confirms / narrows the von..bis (pre-filled to the period).
      if (cell.dataset.cellAggregated === "true") {
        this.openDialog(cell, cell, data)
        return
      }

      // Hourly: the drop creates exactly the slot the ghost previewed — the chosen Dauer at
      // the pointer's time, clipped into the free space there.
      if (this.hourlyValue) {
        this.hideDropGhost()
        if (cell.dataset.cellDate !== this.focusDateValue) return
        const gap = this.slotAtPointer(cell, event.clientX)
        if (!gap) return

        this.post(this.urlValue, {
          employee_id: Number(this.rowOf(cell).employeeId),
          plannable_type: data.resourceType,
          plannable_id: Number(data.resourceId),
          date: cell.dataset.cellDate,
          start_minutes: gap.start,
          end_minutes: gap.end,
        })
        return
      }

      this.post(this.urlValue, {
        employee_id: Number(this.rowOf(cell).employeeId),
        plannable_type: data.resourceType,
        plannable_id: Number(data.resourceId),
        date: cell.dataset.cellDate,
      })
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "planning-employee-board", action: "drop" },
      })
    }
  }

  // Click an empty cell → dialog with the row's employee fixed and a Baustelle/Aufgabe picker.
  // A bar's ✕ routes to the shared styled remove-confirm dialog instead.
  click(event) {
    // Beauftragen for one row: fetches the confirm dialog as a Turbo Stream.
    const dispatchBtn = event.target.closest("[data-planning-dispatch-url]")
    if (dispatchBtn) {
      event.preventDefault()
      this.openDispatchDialog(dispatchBtn.dataset.planningDispatchUrl)
      return
    }

    const removeBtn = event.target.closest("[data-planning-remove]")
    if (removeBtn) {
      event.preventDefault()
      document.dispatchEvent(
        new CustomEvent("planning:confirm-remove", { detail: this.removeDetail(removeBtn) }),
      )
      return
    }

    if (!this.canPlanValue) return
    // A drag-select just finished — its pointerup produces a click we must not act on twice.
    if (this.suppressClick) {
      this.suppressClick = false
      return
    }
    if (event.target.closest("button, a, form, input, select")) return

    // Hourly: a segment opens the exact-times popover; everything else on the grid is inert
    // (context days are read-only, and an empty focused track plans via card drop).
    if (this.hourlyValue) {
      const segment = event.target.closest("[data-hourly-segment]")
      if (segment && segment.dataset.locked !== "true") this.openPopover(segment)
      return
    }

    // Clicking a planned bar edits that run rather than planning a new one. Dispatched
    // (beauftragt) runs are locked — the server rejects every span gesture on them — and so are
    // blockers for a planner who may not write them (data-locked).
    const bar = event.target.closest("[data-planning-bar]")
    if (bar) {
      if (bar.dataset.locked === "true") return
      if (bar.dataset.blockerId) this.openBlockerDialog(bar)
      else if (bar.dataset.dispatched !== "true") this.openEditDialog(bar)
      return
    }

    const cell = this.cellFor(event)
    if (!cell) return
    this.openDialog(cell, cell, null)
  }

  async openDispatchDialog(url) {
    const response = await fetch(url, {
      headers: { Accept: "text/vnd.turbo-stream.html" },
      credentials: "same-origin",
    })
    if (response.ok) Turbo.renderStreamMessage(await response.text())
  }

  // The run, handed to the shared dialog as an edit: it PATCHes set_span with the original extent,
  // so saving reconciles exactly this run instead of creating a second one — or, with another
  // Baustelle/Aufgabe picked, retarget_span moves the whole run there.
  openEditDialog(bar) {
    const data = bar.dataset
    document.dispatchEvent(
      new CustomEvent("planning:plan-range", {
        detail: {
          mode: "plannablePick",
          editMode: true,
          setSpanUrl: this.setSpanUrlValue,
          originalFrom: data.spanFrom,
          originalTo: data.spanTo,
          employeeId: Number(data.employeeId),
          employeeName: data.employeeName,
          resourceType: "Employee",
          resourceId: Number(data.employeeId),
          resourceName: data.employeeName,
          plannableType: data.plannableType,
          plannableId: Number(data.plannableId),
          plannableName: data.plannableName,
          periodStart: data.spanFrom,
          periodEnd: data.spanTo,
          label: data.spanLabel || "",
        },
      }),
    )
  }

  // A blocker type chip dropped on a day: that day blocked (stretch the bar for more). An
  // aggregated cell asks for the range first, like a Baustelle drop does there.
  dropBlocker(cell, data) {
    const row = this.rowOf(cell)
    if (cell.dataset.cellAggregated === "true") {
      const { start, end } = this.boundsFor(cell)
      document.dispatchEvent(
        new CustomEvent("planning:plan-blocker", {
          detail: {
            employeeId: Number(row.employeeId),
            employeeName: row.employeeName,
            blockerTypeId: data.resourceId,
            periodStart: start,
            periodEnd: end,
          },
        }),
      )
      return
    }

    this.post(this.blockerUrlValue, {
      capacity_blocker: {
        employee_id: Number(row.employeeId),
        capacity_blocker_type_id: Number(data.resourceId),
        starts_on: cell.dataset.cellDate,
        ends_on: cell.dataset.cellDate,
      },
    })
  }

  // A blocker bar opens the blocker dialog on that blocker.
  openBlockerDialog(bar) {
    const data = bar.dataset
    document.dispatchEvent(
      new CustomEvent("planning:plan-blocker", {
        detail: {
          blockerUrl: data.blockerUrl,
          blockerTypeId: Number(data.blockerTypeId),
          blockerTypeName: data.blockerTypeName,
          employeeId: Number(data.employeeId),
          employeeName: data.employeeName,
          periodStart: data.spanFrom,
          periodEnd: data.spanTo,
          label: data.blockerLabel || "",
        },
      }),
    )
  }

  // Horizontal drag-select across a row's cells: a ghost bar previews the covered range and the
  // dialog opens with von..bis pre-filled. Starts only on a real cell and never leaves its own row.
  selectStart(event) {
    // A gesture released outside the board sends its click to an ancestor, so the flag set for
    // it is never consumed; a new press must not inherit it and lose its own click.
    this.suppressClick = false
    if (!this.canPlanValue || event.button !== 0) return
    if (event.target.closest("button, a, form, input, select")) return
    if (event.target.closest("[data-hourly-popover]")) return

    // Hourly: segments drag on the hour scale (whole-day bands don't — they get times via the
    // popover), and the day-mode drag-select across cells stays off.
    if (this.hourlyValue) {
      const segment = event.target.closest("[data-hourly-segment]")
      if (segment && segment.dataset.untimed !== "true" && segment.dataset.locked !== "true")
        this.hourlyDragStart(event, segment)
      return
    }

    // A bar takes precedence: dragging its body moves the run, dragging an edge resizes it.
    // Dispatched (beauftragt) runs and locked blockers stay inert — no move, no resize, no cell
    // select below.
    const bar = event.target.closest("[data-planning-bar]")
    if (bar) {
      if (bar.dataset.dispatched !== "true" && bar.dataset.locked !== "true")
        this.barDragStart(event, bar)
      return
    }

    const cell = this.cellFor(event)
    const track = cell?.closest("[data-bar-track]")
    if (!track) return

    // A ghost still waiting for its dialog to close belongs to an earlier gesture.
    this.removeSelectionGhost()
    const index = Array.from(cell.parentElement.children).indexOf(cell)
    this.selection = { track, startIndex: index, endIndex: index, moved: false, x: event.clientX }
    this.lockTextSelection()
    window.addEventListener("pointermove", this.boundSelectMove)
    window.addEventListener("pointerup", this.boundSelectEnd)
    window.addEventListener("pointercancel", this.boundGestureCancel)
  }

  // The column comes from the pointer's x on the row's own track, not from the element under it:
  // bars and absence bands cover the cells, and the selection must keep growing across them.
  selectMove(event) {
    if (!this.selection) return
    // Ignore sub-pixel jitter so a plain click still reads as a click.
    if (!this.selection.moved && Math.abs(event.clientX - this.selection.x) < 4) return
    this.selection.moved = true
    const endIndex = this.columnIndexAt(this.selection.track, event.clientX)
    if (endIndex === this.selection.endIndex && this.selectionGhost) return
    this.selection.endIndex = endIndex
    this.paintSelectionGhost(this.selection)
  }

  selectEnd() {
    this.endSelectionListeners()
    const selection = this.selection
    this.selection = null
    if (!selection) return
    if (!selection.moved || selection.startIndex === selection.endIndex) {
      this.removeSelectionGhost()
      return
    }

    // The pointerup also fires a click on the cell — swallow it so the dialog opens once.
    this.suppressClick = true
    const { from, to } = this.selectionRange(selection)
    // The ghost stays while the dialog is open, showing where the plan lands; any close of the
    // dialog (save, cancel, switch to Blocker) takes it away — a save re-renders the row anyway.
    document.addEventListener("planning:plan-range-closed", this.boundRemoveSelectionGhost, {
      once: true,
    })
    this.planRange(selection.track.closest("[data-employee-row]")?.dataset || {}, from, to, null)
  }

  endSelectionListeners() {
    this.unlockTextSelection()
    window.removeEventListener("pointermove", this.boundSelectMove)
    window.removeEventListener("pointerup", this.boundSelectEnd)
    window.removeEventListener("pointercancel", this.boundGestureCancel)
  }

  // The selected columns' dates in visual order, regardless of drag direction.
  selectionRange({ startIndex, endIndex }) {
    const first = Math.min(startIndex, endIndex)
    const last = Math.max(startIndex, endIndex)
    return { first, last, from: this.periodsValue[first]?.start, to: this.periodsValue[last]?.end }
  }

  // One full-height dashed band over the selected columns (lanes included), labelled with the
  // dates the dialog will open on. pointer-events-none keeps it out of every hit test. No z-index:
  // the track opens no stacking context, so a z-10 here would tie with the sticky identity cell
  // and, coming later in the DOM, paint over it once the board scrolls sideways.
  paintSelectionGhost(selection) {
    const total = this.periodsValue.length
    const { first, last, from, to } = this.selectionRange(selection)
    if (!total || !from || !to) return

    if (!this.selectionGhost) {
      this.selectionGhost = document.createElement("div")
      this.selectionGhost.dataset.selectionGhost = ""
      this.selectionGhost.className =
        "pointer-events-none absolute inset-y-0 rounded border-2 border-dashed border-green-500 bg-green-100/60"
      const label = document.createElement("span")
      label.className =
        "absolute left-1 top-1 whitespace-nowrap rounded bg-white/90 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-green-800 shadow-xs"
      this.selectionGhost.appendChild(label)
    }
    if (this.selectionGhost.parentElement !== selection.track)
      selection.track.appendChild(this.selectionGhost)

    this.selectionGhost.style.left = `${(first / total) * 100}%`
    this.selectionGhost.style.width = `${((last - first + 1) / total) * 100}%`
    this.selectionGhost.firstChild.textContent = this.rangeLabel(from, to)
  }

  removeSelectionGhost() {
    document.removeEventListener("planning:plan-range-closed", this.boundRemoveSelectionGhost)
    this.selectionGhost?.remove()
    this.selectionGhost = null
  }

  // "02.03. – 28.03.2027"; the start keeps its year only when the range crosses a new year.
  rangeLabel(from, to) {
    const start = this.germanDate(from)
    return `${from.slice(0, 4) === to.slice(0, 4) ? start.slice(0, 6) : start} – ${this.germanDate(to)}`
  }

  // Open the shared "Planung" dialog. `data` is a dropped Baustelle/Aufgabe payload (the plannable
  // is then fixed), or null so the dialog shows its Baustelle/Aufgabe picker.
  openDialog(fromCell, toCell, data) {
    this.planRange(
      this.rowOf(fromCell),
      this.boundsFor(fromCell).start,
      this.boundsFor(toCell).end,
      data,
    )
  }

  // `row` is the employee row's dataset (data-employee-id / -name).
  planRange(row, from, to, data) {
    document.dispatchEvent(
      new CustomEvent("planning:plan-range", {
        detail: {
          mode: "plannablePick",
          employeeId: Number(row.employeeId),
          employeeName: row.employeeName,
          resourceName: row.employeeName,
          plannableType: data?.resourceType,
          plannableId: data?.resourceId,
          plannableName: data?.resourceName,
          periodStart: from,
          periodEnd: to,
        },
      }),
    )
  }

  // A day cell is a single day; an aggregated cell spans its period.
  boundsFor(cell) {
    if (cell.dataset.cellAggregated === "true") {
      return { start: cell.dataset.cellPeriodStart, end: cell.dataset.cellPeriodEnd }
    }
    return { start: cell.dataset.cellDate, end: cell.dataset.cellDate }
  }

  // A pointer drag is also a text selection to the browser, which would highlight the names and
  // bar labels it sweeps over. Applied on pointerdown — before the browser anchors a selection —
  // by each gesture's start, and lifted again by that gesture's end-listener cleanup.
  lockTextSelection() {
    this.element.classList.add("select-none")
  }

  unlockTextSelection() {
    this.element.classList.remove("select-none")
  }

  // A pointercancel (a touch taken over by scrolling, the OS claiming the pointer) ends the gesture
  // without a drop: nothing is saved, and every preview returns to what is actually planned.
  gestureCancel() {
    const { selection, drag, hourlyDrag } = this
    this.selection = null
    this.drag = null
    this.hourlyDrag = null
    this.endSelectionListeners()
    this.endDragListeners()
    this.endHourlyDragListeners()

    if (selection) this.removeSelectionGhost()
    if (drag) {
      drag.bar.classList.remove("opacity-70", "shadow-md")
      this.clearRowHighlight()
      if (drag.moved) this.revertPreview(drag)
    }
    if (hourlyDrag) {
      hourlyDrag.segment.classList.remove("opacity-70", "shadow-md")
      if (hourlyDrag.moved)
        this.previewSegment({ ...hourlyDrag, newFrom: hourlyDrag.from, newTo: hourlyDrag.to })
    }
  }

  // ---------------------------------------------------------------------------------------------
  // Bar move / resize. Both reshape the run through set_span, which reconciles the daily
  // allocations; a move onto another employee's row is a create on the target plus a clear on the
  // source, since a run belongs to one employee by construction.
  // ---------------------------------------------------------------------------------------------

  barDragStart(event, bar) {
    const handle = event.target.closest("[data-bar-resize]")
    const row = bar.closest("[data-employee-row]")
    const track = bar.parentElement

    this.drag = {
      bar,
      track,
      sourceRow: row,
      targetRow: row,
      top: bar.style.top,
      edge: handle?.dataset.barResize || null,
      startX: event.clientX,
      from: bar.dataset.spanFrom,
      to: bar.dataset.spanTo,
      newFrom: bar.dataset.spanFrom,
      newTo: bar.dataset.spanTo,
      moved: false,
      startIndex: this.columnIndexAt(track, event.clientX),
    }

    this.lockTextSelection()
    window.addEventListener("pointermove", this.boundDragMove)
    window.addEventListener("pointerup", this.boundDragEnd)
    window.addEventListener("pointercancel", this.boundGestureCancel)
  }

  dragMove(event) {
    const drag = this.drag
    if (!drag) return
    if (!drag.moved && Math.abs(event.clientX - drag.startX) < 4) return
    drag.moved = true
    drag.bar.classList.add("opacity-70", "shadow-md")

    const index = this.columnIndexAt(drag.track, event.clientX)
    const delta = index - drag.startIndex

    if (drag.edge === "start") {
      drag.newFrom = this.shiftDate(drag.from, delta, "start", { max: drag.to })
    } else if (drag.edge === "end") {
      drag.newTo = this.shiftDate(drag.to, delta, "end", { min: drag.from })
    } else if (drag.bar.dataset.blockerId) {
      // A blocker is a literal date range: a move keeps its length instead of snapping out to
      // whole buckets the way a run does (set_span refills a run's working days, nothing refills
      // a blocker's). Shifted by the calendar days between the source and the landing column.
      const days = this.daysBetweenColumns(drag.from, delta)
      drag.newFrom = this.plainShift(drag.from, days)
      drag.newTo = this.plainShift(drag.to, days)
      this.highlightTargetRow(event)
    } else {
      drag.newFrom = this.dateAtOffset(drag.from, delta, "start")
      drag.newTo = this.dateAtOffset(drag.to, delta, "end")
      this.highlightTargetRow(event)
    }

    this.previewBar(drag)
  }

  async dragEnd() {
    this.endDragListeners()
    const drag = this.drag
    this.drag = null
    if (!drag) return

    drag.bar.classList.remove("opacity-70", "shadow-md")
    this.clearRowHighlight()
    // A press that never moved is a click — let the click handler open the edit dialog.
    if (!drag.moved) return

    this.suppressClick = true
    const targetEmployeeId = Number(
      drag.targetRow?.dataset.employeeId || drag.bar.dataset.employeeId,
    )
    const sourceEmployeeId = Number(drag.bar.dataset.employeeId)
    const unchanged =
      drag.newFrom === drag.from && drag.newTo === drag.to && targetEmployeeId === sourceEmployeeId
    if (unchanged) {
      this.previewBar({ ...drag, newFrom: drag.from, newTo: drag.to })
      return
    }

    // A blocker is one row, not a run of daily rows: move, resize and reassign are a single PATCH.
    if (drag.bar.dataset.blockerId) {
      const ok = await this.post(
        drag.bar.dataset.blockerUrl,
        {
          capacity_blocker: {
            employee_id: targetEmployeeId,
            starts_on: drag.newFrom,
            ends_on: drag.newTo,
          },
        },
        "PATCH",
      )
      if (!ok) this.revertPreview(drag)
      return
    }

    const plannable = {
      plannable_type: drag.bar.dataset.plannableType,
      plannable_id: Number(drag.bar.dataset.plannableId),
    }

    if (targetEmployeeId === sourceEmployeeId) {
      // working_days_only mirrors the create flow so a move/resize never fills
      // weekends/holidays (same flag the Zeitstrahl drag sends).
      const ok = await this.post(
        this.setSpanUrlValue,
        {
          ...plannable,
          employee_id: sourceEmployeeId,
          date_from: drag.newFrom,
          date_to: drag.newTo,
          original_from: drag.from,
          original_to: drag.to,
          working_days_only: "1",
        },
        "PATCH",
      )
      if (!ok) this.revertPreview(drag)
      return
    }

    // Reassignment: one endpoint plans the target and clears the source in a single transaction —
    // and a single undo entry, so one Ctrl+Z takes the whole gesture back. A failure leaves the
    // board untouched; snap the preview back to where the source bar actually is.
    const ok = await this.post(
      this.reassignSpanUrlValue,
      {
        ...plannable,
        employee_id: sourceEmployeeId,
        target_employee_id: targetEmployeeId,
        date_from: drag.newFrom,
        date_to: drag.newTo,
        original_from: drag.from,
        original_to: drag.to,
        working_days_only: "1",
      },
      "PATCH",
    )
    if (!ok) this.revertPreview(drag)
  }

  // Undo the optimistic drag preview: the bar snaps back to the row and dates nothing was saved for.
  revertPreview(drag) {
    this.placeBarInRow(drag, drag.sourceRow)
    this.previewBar({ ...drag, newFrom: drag.from, newTo: drag.to })
  }

  endDragListeners() {
    this.unlockTextSelection()
    window.removeEventListener("pointermove", this.boundDragMove)
    window.removeEventListener("pointerup", this.boundDragEnd)
    window.removeEventListener("pointercancel", this.boundGestureCancel)
  }

  // Live feedback while dragging: the bar itself is repositioned over the columns it would land on.
  // The weekend gaps inside it are positioned relative to the bar's ORIGINAL extent, so they hide
  // while it is reshaped (they'd stretch onto the wrong days) and show again once it is back where
  // it started; the server re-render after a save redraws them for the new run.
  previewBar(drag) {
    const total = this.periodsValue.length
    if (!total) return

    const first = this.columnIndexOfDate(drag.newFrom)
    const last = this.columnIndexOfDate(drag.newTo)
    if (first === null || last === null) return

    drag.bar.style.left = `${(first / total) * 100}%`
    drag.bar.style.width = `${((last - first + 1) / total) * 100}%`
    const reshaped = drag.newFrom !== drag.from || drag.newTo !== drag.to
    drag.bar.querySelectorAll("[data-timeline-gap]").forEach((gap) => (gap.hidden = reshaped))
  }

  highlightTargetRow(event) {
    const row = document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest("[data-employee-row]")
    if (!row || row === this.drag.targetRow) return

    this.clearRowHighlight()
    this.drag.targetRow = row
    if (row !== this.drag.sourceRow) row.classList.add("bg-green-50")
    this.placeBarInRow(this.drag, row)
  }

  // The preview follows the pointer onto another employee's row: the bar itself is re-parented
  // into that row's track, so the planner sees where the run would land — not just a tinted row.
  // On success the server re-renders both rows; on failure revertPreview puts it back.
  placeBarInRow(drag, row) {
    const track = row === drag.sourceRow ? drag.track : row.querySelector("[data-bar-track]")
    if (!track) return
    if (drag.bar.parentElement !== track) track.appendChild(drag.bar)

    if (row === drag.sourceRow) {
      drag.bar.style.top = drag.top
      return
    }
    // Keep the bar's lane where the target row has room for it, else the lowest lane that fits —
    // a bar from a third lane must not hang out of a single-lane row into the next employee.
    // The inset is RowComponent::ROW_PADDING, rendered onto the track so the two never drift apart.
    const inset = Number(track.dataset.barTrackInset) || 0
    const fits = track.clientHeight - drag.bar.offsetHeight - inset
    drag.bar.style.top = `${Math.max(inset, Math.min(parseFloat(drag.top) || 0, fits))}px`
  }

  clearRowHighlight() {
    this.element
      .querySelectorAll("[data-employee-row].bg-green-50")
      .forEach((row) => row.classList.remove("bg-green-50"))
  }

  // Column index under a pointer x, clamped to the rendered columns.
  columnIndexAt(track, clientX) {
    const rect = track.getBoundingClientRect()
    const total = this.periodsValue.length || 1
    const index = Math.floor(((clientX - rect.left) / rect.width) * total)
    return Math.min(Math.max(index, 0), total - 1)
  }

  columnIndexOfDate(iso) {
    const index = this.periodsValue.findIndex((p) => iso >= p.start && iso <= p.end)
    return index === -1 ? null : index
  }

  // Move a date by `delta` COLUMNS (a column is a day, or a whole bucket when aggregated), keeping
  // the run non-empty: an edge can never cross the opposite one.
  shiftDate(iso, delta, edge, { min = null, max = null } = {}) {
    const moved = this.dateAtOffset(iso, delta, edge)
    if (max && moved > max) return max
    if (min && moved < min) return min
    return moved
  }

  // `edge` decides where inside the landing column the date sits, which only differs when the
  // columns are buckets (week/month/quarter): a run's start snaps to the bucket's first day and its
  // end to the last, so a dragged bar still covers whole buckets.
  dateAtOffset(iso, delta, edge) {
    if (!delta) return iso

    const index = this.columnIndexOfDate(iso)
    if (index === null) return this.plainShift(iso, delta)

    const target =
      this.periodsValue[Math.min(Math.max(index + delta, 0), this.periodsValue.length - 1)]
    return edge === "end" ? target.end : target.start
  }

  // Calendar days from the column holding `iso` to the column `delta` further on — `delta` days on
  // the day grid, the distance between the two buckets' first days when aggregated.
  daysBetweenColumns(iso, delta) {
    const index = this.columnIndexOfDate(iso)
    if (!delta || index === null) return delta

    const target = Math.min(Math.max(index + delta, 0), this.periodsValue.length - 1)
    const from = new Date(`${this.periodsValue[index].start}T00:00:00`)
    const to = new Date(`${this.periodsValue[target].start}T00:00:00`)
    return Math.round((to - from) / 86400000)
  }

  // Calendar-day fallback for a run whose edge lies outside the rendered window. Formatted from the
  // LOCAL parts — toISOString() would shift midnight into the previous UTC day east of Greenwich.
  plainShift(iso, delta) {
    const date = new Date(`${iso}T00:00:00`)
    date.setDate(date.getDate() + delta)
    const month = `${date.getMonth() + 1}`.padStart(2, "0")
    const day = `${date.getDate()}`.padStart(2, "0")
    return `${date.getFullYear()}-${month}-${day}`
  }

  // ---------------------------------------------------------------------------------------------
  // Hourly mode (Stundenmodus): timed segments on the focused day's hour scale.
  // ---------------------------------------------------------------------------------------------

  // Largest slot between the cell's timed segments on the visible scale, snapped inward to the
  // 30-minute grid — where a dropped card lands. Whole-day bands don't block (mixing is the
  // sanctioned warn-only state); null when no snapped slot of at least one step remains.
  // The slot a dropped card gets — and the ghost previews: it starts at the pointer's minute
  // (snapped) and runs for the panel's chosen Dauer. Only segments of the SAME Baustelle/
  // Aufgabe block it (they are the hard server rule) — dropping onto another site's stretch
  // is legitimate double-booking, shown as stacked lanes with the warn ring, so the slot is
  // pushed past and clipped against same-plannable segments only. Less than one snap step of
  // room means no slot: the ghost disappears and the drop is refused, never relocated.
  slotAtPointer(cell, clientX) {
    const track = cell.querySelector("[data-hourly-track]")
    if (!track) return null

    const taken = this.takenIntervals(cell, this.draggedPlannableKey())
    let start = Math.max(this.snap(this.minutesAt(track, clientX)), this.windowStartValue)
    for (const [from, to] of taken) {
      if (start >= from && start < to) start = Math.ceil(to / SNAP_MINUTES) * SNAP_MINUTES
    }

    const nextStart = Math.min(
      this.windowEndValue,
      ...taken.map(([from]) => from).filter((from) => from >= start),
    )
    const end = Math.min(start + this.dropDurationMinutes(), nextStart)
    return end - start >= SNAP_MINUTES ? { start, end } : null
  }

  // Stashed by the panel's dragstart, since dragover cannot read the drag payload. Without a
  // stash (unexpected drag source) every segment blocks — the safe direction.
  draggedPlannableKey() {
    return document.documentElement.dataset.planningDragPlannable || null
  }

  takenIntervals(cell, plannableKey) {
    return Array.from(cell.querySelectorAll("[data-hourly-segment]"))
      .filter((segment) => segment.dataset.untimed !== "true")
      .filter((segment) => !plannableKey || segment.dataset.plannableKey === plannableKey)
      .map((segment) => [Number(segment.dataset.startMinutes), Number(segment.dataset.endMinutes)])
      .sort((a, b) => a[0] - b[0])
  }

  // The Dauer chips in the plannable panel write this key; 30 when never chosen or when
  // storage is unavailable.
  dropDurationMinutes() {
    try {
      const stored = Number(localStorage.getItem(DURATION_STORAGE_KEY))
      return [30, 60, 90, 120].includes(stored) ? stored : FALLBACK_DROP_MINUTES
    } catch {
      return FALLBACK_DROP_MINUTES
    }
  }

  showDropGhost(cell, slot) {
    const track = cell.querySelector("[data-hourly-track]")
    if (!track) return

    if (!this.dropGhost) {
      this.dropGhost = document.createElement("div")
      this.dropGhost.className =
        "pointer-events-none absolute inset-y-[3px] z-10 rounded border-2 border-dashed border-green-500 bg-green-100/60"
    }
    if (this.dropGhost.parentElement !== track) track.appendChild(this.dropGhost)
    const span = this.windowEndValue - this.windowStartValue
    this.dropGhost.style.left = `${((slot.start - this.windowStartValue) / span) * 100}%`
    this.dropGhost.style.width = `${((slot.end - slot.start) / span) * 100}%`
  }

  hideDropGhost() {
    this.dropGhost?.remove()
  }

  hourlyDragStart(event, segment) {
    this.closePopover()
    const handle = event.target.closest("[data-segment-resize]")
    const track = segment.closest("[data-hourly-track]")
    if (!track) return

    this.hourlyDrag = {
      segment,
      track,
      edge: handle?.dataset.segmentResize || null,
      startPointerMinutes: this.minutesAt(track, event.clientX),
      from: Number(segment.dataset.startMinutes),
      to: Number(segment.dataset.endMinutes),
      newFrom: Number(segment.dataset.startMinutes),
      newTo: Number(segment.dataset.endMinutes),
      moved: false,
      startX: event.clientX,
    }
    this.lockTextSelection()
    window.addEventListener("pointermove", this.boundHourlyDragMove)
    window.addEventListener("pointerup", this.boundHourlyDragEnd)
    window.addEventListener("pointercancel", this.boundGestureCancel)
  }

  hourlyDragMove(event) {
    const drag = this.hourlyDrag
    if (!drag) return
    if (!drag.moved && Math.abs(event.clientX - drag.startX) < 4) return
    drag.moved = true
    drag.segment.classList.add("opacity-70", "shadow-md")

    const pointer = this.minutesAt(drag.track, event.clientX)
    if (drag.edge === "start") {
      drag.newFrom = Math.min(this.snap(pointer), drag.to - SNAP_MINUTES)
      drag.newFrom = Math.max(drag.newFrom, this.windowStartValue)
    } else if (drag.edge === "end") {
      drag.newTo = Math.max(this.snap(pointer), drag.from + SNAP_MINUTES)
      drag.newTo = Math.min(drag.newTo, this.windowEndValue)
    } else {
      const duration = drag.to - drag.from
      // A segment longer than the visible window cannot be moved on this scale — the clamp
      // below would pin newFrom BELOW windowStart and silently rewrite the times. Leave it
      // in place; the exact-times popover is the edit path for such a segment.
      if (duration > this.windowEndValue - this.windowStartValue) return
      const shifted = drag.from + this.snap(pointer - drag.startPointerMinutes)
      drag.newFrom = Math.min(
        Math.max(shifted, this.windowStartValue),
        this.windowEndValue - duration,
      )
      drag.newTo = drag.newFrom + duration
    }
    this.previewSegment(drag)
  }

  async hourlyDragEnd() {
    this.endHourlyDragListeners()
    const drag = this.hourlyDrag
    this.hourlyDrag = null
    if (!drag) return

    drag.segment.classList.remove("opacity-70", "shadow-md")
    // A press that never moved is a click — the click handler opens the popover.
    if (!drag.moved) return

    this.suppressClick = true
    if (drag.newFrom === drag.from && drag.newTo === drag.to) return

    const ok = await this.post(
      drag.segment.dataset.updateUrl,
      { start_minutes: drag.newFrom, end_minutes: drag.newTo },
      "PATCH",
    )
    if (!ok) this.previewSegment({ ...drag, newFrom: drag.from, newTo: drag.to })
  }

  endHourlyDragListeners() {
    this.unlockTextSelection()
    window.removeEventListener("pointermove", this.boundHourlyDragMove)
    window.removeEventListener("pointerup", this.boundHourlyDragEnd)
    window.removeEventListener("pointercancel", this.boundGestureCancel)
  }

  previewSegment(drag) {
    const span = this.windowEndValue - this.windowStartValue
    drag.segment.style.left = `${((drag.newFrom - this.windowStartValue) / span) * 100}%`
    drag.segment.style.width = `${((drag.newTo - drag.newFrom) / span) * 100}%`
  }

  // Minute-of-day under a pointer x on a focused track, clamped to the visible scale.
  minutesAt(track, clientX) {
    const rect = track.getBoundingClientRect()
    const span = this.windowEndValue - this.windowStartValue
    const minutes = this.windowStartValue + ((clientX - rect.left) / rect.width) * span
    return Math.min(Math.max(minutes, this.windowStartValue), this.windowEndValue)
  }

  snap(minutes) {
    return Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES
  }

  // --- exact-times popover ---------------------------------------------------------------------

  popover() {
    return this.element.querySelector("[data-hourly-popover]")
  }

  wirePopover() {
    const popover = this.popover()
    if (!popover) return

    popover
      .querySelector("[data-hourly-popover-save]")
      .addEventListener("click", () => this.popoverSave())
    popover
      .querySelector("[data-hourly-popover-cancel]")
      .addEventListener("click", () => this.closePopover())
    popover
      .querySelector("[data-hourly-popover-delete]")
      .addEventListener("click", () => this.popoverDelete())
    popover.querySelectorAll("input[type=time]").forEach((input) => {
      input.addEventListener("input", () => this.popoverDurationHint())
    })
  }

  openPopover(segment) {
    const popover = this.popover()
    if (!popover) return

    this.popoverSegment = segment
    const cell = segment.closest("[data-planning-cell]")
    popover.querySelector("[data-hourly-popover-label]").textContent = segment.dataset.label
    popover.querySelector("[data-hourly-popover-subtitle]").textContent = [
      cell && this.rowOf(cell).employeeName,
      this.formatDate(cell?.dataset.cellDate),
    ]
      .filter(Boolean)
      .join(" · ")
    // The dot mirrors the segment's identity colour (red border while in conflict included).
    popover.querySelector("[data-hourly-popover-dot]").style.backgroundColor =
      getComputedStyle(segment).borderColor
    // A whole-day band opens with the un-padded work window prefilled — saving gives it times.
    const from = segment.dataset.startMinutes || this.windowStartValue + 60
    const to = segment.dataset.endMinutes || this.windowEndValue - 60
    popover.querySelector("[data-hourly-popover-from]").value = this.toHHMM(Number(from))
    popover.querySelector("[data-hourly-popover-to]").value = this.toHHMM(Number(to))
    popover.querySelector("[data-hourly-popover-error]").classList.add("hidden")
    popover.querySelector("[data-hourly-popover-allocation-label]").value =
      segment.dataset.allocationLabel || ""
    this.popoverDurationHint()
    this.positionPopover(popover, segment)
    popover.classList.remove("hidden")
    document.addEventListener("pointerdown", this.boundPopoverDismiss)
  }

  positionPopover(popover, segment) {
    const boardRect = this.element.getBoundingClientRect()
    const rect = segment.getBoundingClientRect()
    popover.style.top = `${rect.bottom - boardRect.top + 6}px`
    const width = 288
    const left = Math.min(Math.max(rect.left - boardRect.left, 8), boardRect.width - width - 8)
    popover.style.left = `${left}px`
  }

  // The rows scroll inside the board, not the page, so an open popover has to follow its segment —
  // and close once the segment has scrolled out of sight, or it would float over the search bar
  // above the box or the bottom panel below it.
  repositionPopover() {
    if (!this.popoverSegment) return
    if (this.segmentScrolledAway(this.popoverSegment)) this.closePopover()
    else this.positionPopover(this.popover(), this.popoverSegment)
  }

  // Under the sticky time header counts as gone: the header covers it.
  segmentScrolledAway(segment) {
    const box = segment.closest("[data-planning-board-scroll]")
    if (!box) return false
    const boxRect = box.getBoundingClientRect()
    const header = box.querySelector("[data-planning-grid]")
    const visibleTop = header ? header.getBoundingClientRect().bottom : boxRect.top
    const rect = segment.getBoundingClientRect()
    return rect.bottom <= visibleTop || rect.top >= boxRect.bottom
  }

  // "2026-08-25" → "25.08." — the popover subtitle's day, formatted without Date parsing
  // (which would shift around midnight east of UTC).
  formatDate(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "")
    return match ? `${match[3]}.${match[2]}.` : null
  }

  closePopover() {
    this.popover()?.classList.add("hidden")
    this.popoverSegment = null
    document.removeEventListener("pointerdown", this.boundPopoverDismiss)
  }

  popoverDismiss(event) {
    if (event.target.closest("[data-hourly-popover]")) return
    // Clicking another segment re-opens on it via the click handler; just close here.
    this.closePopover()
  }

  popoverMinutes() {
    const popover = this.popover()
    const from = this.fromHHMM(popover.querySelector("[data-hourly-popover-from]").value)
    let to = this.fromHHMM(popover.querySelector("[data-hourly-popover-to]").value)
    // App-wide convention (see HourWindow): a Bis of "00:00" means end of day — the native
    // time input cannot express 24:00, so 1440-minute ends round-trip through "00:00".
    if (to === 0) to = 1440
    return { from, to }
  }

  popoverDurationHint() {
    const { from, to } = this.popoverMinutes()
    const hint = this.popover().querySelector("[data-hourly-popover-duration]")
    if (from === null || to === null || to <= from) {
      hint.textContent = ""
      return
    }
    const hours = ((to - from) / 60).toFixed(1).replace(".", ",")
    hint.textContent = `= ${hours} h`
  }

  async popoverSave() {
    const segment = this.popoverSegment
    if (!segment) return
    const { from, to } = this.popoverMinutes()
    if (from === null || to === null || to <= from) {
      this.popover().querySelector("[data-hourly-popover-error]").classList.remove("hidden")
      return
    }

    const label = this.popover().querySelector("[data-hourly-popover-allocation-label]").value
    const ok = await this.post(
      segment.dataset.updateUrl,
      { start_minutes: from, end_minutes: to, label: label.trim() },
      "PATCH",
    )
    if (ok) this.closePopover()
  }

  // Routes to the shared styled remove-confirm dialog — same flow as a chip's ✕.
  popoverDelete() {
    const segment = this.popoverSegment
    if (!segment) return
    this.closePopover()
    document.dispatchEvent(
      new CustomEvent("planning:confirm-remove", {
        detail: { url: segment.dataset.updateUrl, title: segment.dataset.removeTitle },
      }),
    )
  }

  toHHMM(minutes) {
    // "24:00" is a valid end_minutes value (end of day) but <input type="time"> silently
    // rejects it, blanking the Bis field — render it as "00:00" instead, which
    // popoverMinutes maps back to 1440.
    if (minutes === 1440) return "00:00"
    const hours = `${Math.floor(minutes / 60)}`.padStart(2, "0")
    return `${hours}:${`${minutes % 60}`.padStart(2, "0")}`
  }

  fromHHMM(value) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value || "")
    if (!match) return null
    return Number(match[1]) * 60 + Number(match[2])
  }

  async post(url, body, method = "POST") {
    const { ok } = await planningMutation({
      url,
      method,
      body,
      component: "planning-employee-board",
    })
    return ok
  }

  // The employee a cell belongs to lives on its row (data-employee-id / -name), not on every cell.
  rowOf(cell) {
    return cell.closest("[data-employee-row]")?.dataset || {}
  }

  // A bar's ✕ carries no request of its own: clear_span over exactly this run, with the same
  // confirm texts the server used to render per bar. Other remove buttons still carry theirs.
  removeDetail(button) {
    const bar = button.dataset.removeUrl ? null : button.closest("[data-planning-bar]")
    if (!bar) {
      return {
        url: button.dataset.removeUrl,
        title: button.dataset.removeTitle,
        body: button.dataset.removeBody,
      }
    }
    const data = bar.dataset
    // Alphabetical, the order Rails' path helper writes (and the server rendered before).
    const query = new URLSearchParams({
      date_from: data.spanFrom,
      date_to: data.spanTo,
      employee_id: data.employeeId,
      plannable_id: data.plannableId,
      plannable_type: data.plannableType,
    })
    return {
      url: urlWithQuery(this.clearSpanUrlValue, query),
      title: this.removeTitleValue,
      body: this.interpolate(this.removeBodyValue, {
        name: data.employeeName,
        plannable: data.barLabel,
      }),
    }
  }

  // The hover card of a bar without a conflict, built from its label, run and PT the first time
  // it is hovered — the same card AllocationBarComponent#tooltip_data renders for conflict bars.
  fillBarTooltip(event) {
    const bar = event.target.closest?.("[data-planning-bar]")
    if (!bar || bar.hasAttribute("data-plantafel-tooltip-data-value")) return
    const data = bar.dataset
    const card = {
      name: data.barLabel,
      items: [
        {
          icon: "calendar",
          text: `${this.germanDate(data.spanFrom)} – ${this.germanDate(data.spanTo)}`,
        },
        { icon: "info", text: this.interpolate(this.tooltipPtValue, { value: data.barPt }) },
        { icon: "task", text: data.employeeName },
      ],
    }
    if (data.spanLabel) card.items.push({ icon: "label", text: data.spanLabel })
    bar.setAttribute("data-plantafel-tooltip-data-value", JSON.stringify(card))
  }

  // Fills I18n-style %{key} placeholders in one pass. A replacement function, not a string:
  // String#replace would read `$&`, `$'` or `$$` in a site or employee name as a pattern, and a
  // second .replace would substitute a placeholder that happened to appear inside a name.
  interpolate(template, values) {
    return template.replace(/%\{(\w+)\}/g, (match, key) =>
      key in values ? String(values[key] ?? "") : match,
    )
  }

  // ISO date → the board's dd.mm.yyyy (the :default date format).
  germanDate(iso) {
    const [year, month, day] = (iso || "").split("-")
    return year ? `${day}.${month}.${year}` : ""
  }
}
